/**
 * Utilidades para generar avatares consistentes
 * Genera degradados determinísticos basados en hash del nombre
 * 100% local - sin solicitudes externas
 */

// Paleta de degradados vibrantes (24 combinaciones)
const GRADIENT_PAIRS = [
  // Azules
  ['#3b82f6', '#1d4ed8'], // blue
  ['#0ea5e9', '#0369a1'], // sky
  ['#06b6d4', '#0e7490'], // cyan
  // Violetas/Púrpuras
  ['#8b5cf6', '#6d28d9'], // violet
  ['#a855f7', '#7c3aed'], // purple
  ['#6366f1', '#4338ca'], // indigo
  // Rosas/Rojos
  ['#ec4899', '#be185d'], // pink
  ['#f43f5e', '#be123c'], // rose
  ['#ef4444', '#b91c1c'], // red
  // Naranjas/Amarillos
  ['#f97316', '#c2410c'], // orange
  ['#f59e0b', '#b45309'], // amber
  ['#eab308', '#a16207'], // yellow
  // Verdes
  ['#22c55e', '#15803d'], // green
  ['#10b981', '#047857'], // emerald
  ['#14b8a6', '#0f766e'], // teal
  // Combinaciones únicas
  ['#8b5cf6', '#ec4899'], // violet-pink
  ['#6366f1', '#8b5cf6'], // indigo-violet
  ['#3b82f6', '#8b5cf6'], // blue-violet
  ['#06b6d4', '#3b82f6'], // cyan-blue
  ['#10b981', '#06b6d4'], // emerald-cyan
  ['#f97316', '#ef4444'], // orange-red
  ['#ec4899', '#f43f5e'], // pink-rose
  ['#22c55e', '#10b981'], // green-emerald
  ['#f59e0b', '#f97316'], // amber-orange
]

/**
 * Genera un hash numérico determinístico a partir de un string
 */
function hashString(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return Math.abs(hash)
}

/**
 * Obtiene datos del avatar (colores e iniciales) de forma determinística
 * @param {string} name - Nombre del contacto
 * @param {string} phone - Teléfono (usado como diferenciador único)
 * @returns {object} - { initials, gradient, colors }
 */
export function getAvatarData(name, phone) {
  const displayName = name || phone || '??'
  // Usar teléfono como diferenciador único (dos "Juan" con distinto teléfono = distinto color)
  const uniqueKey = phone || displayName
  const hash = hashString(uniqueKey)
  const gradientIndex = hash % GRADIENT_PAIRS.length
  const [colorFrom, colorTo] = GRADIENT_PAIRS[gradientIndex]

  return {
    initials: getInitials(displayName),
    gradient: `linear-gradient(135deg, ${colorFrom} 0%, ${colorTo} 100%)`,
    colorFrom,
    colorTo,
    displayName
  }
}

/**
 * Obtiene las iniciales de un nombre (máximo 2 caracteres)
 * Para uso en fallback o cuando no se puede cargar la imagen
 * @param {string} name - Nombre completo
 * @param {string} fallback - Valor de fallback si no hay nombre
 * @returns {string} - Iniciales (1-2 caracteres)
 */
export function getInitials(name, fallback = '??') {
  if (!name || typeof name !== 'string') return fallback

  const cleanName = name.trim()

  // Si es un número de teléfono, usar los últimos 2 dígitos
  if (/^\+?\d+$/.test(cleanName.replace(/\s/g, ''))) {
    const digits = cleanName.replace(/\D/g, '')
    return digits.slice(-2) || fallback
  }

  // Dividir por espacios y obtener iniciales
  const words = cleanName.split(/\s+/).filter(w => w.length > 0)

  if (words.length === 0) return fallback

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }

  return (words[0][0] + words[1][0]).toUpperCase()
}
